import { useState, useMemo } from 'react';
import { useRouter } from '@uirouter/react';

import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { useEnvironmentList } from '@/react/portainer/environments/queries';
import type { Environment } from '@/react/portainer/environments/types';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { PageHeader } from '@@/PageHeader';
import { Stepper } from '@@/Stepper/Stepper';

import {
  getEnvironmentTypeDisplayName,
  getPolicyTypeDisplayName,
  PolicyType,
  EnvironmentTypeFilter,
  CategoryFilter,
  PolicyTemplate,
} from '../types';
import { useCreatePolicyMutation } from '../queries/useCreatePolicyMutation';
import {
  getEnvironmentTypeFromPolicyType,
  getCompatibleGroupsCount,
  getVisiblePolicyTypes,
} from '../utils/policyTypeUtils';
import { getAllPolicyTypesMetadata } from '../utils/policy-types-metadata';
import { filterPolicyTypes } from '../utils/filterPolicyTypes';
import { buildKubernetesRBACPayload } from '../components/kubernetes/rbac/buildKubernetesRBACPayload';
import { buildKubernetesSecurityPayload } from '../components/kubernetes/security/buildKubernetesSecurityPayload';
import { buildKubernetesSetupPayload } from '../components/kubernetes/setup/buildKubernetesSetupPayload';
import { buildKubernetesRegistryPayload } from '../components/kubernetes/registry/buildKubernetesRegistryPayload';
import { buildDockerRBACPayload } from '../components/docker/rbac/buildDockerRBACPayload';
import { buildDockerSecurityPayload } from '../components/docker/security/buildDockerSecurityPayload';
import { buildDockerSetupPayload } from '../components/docker/setup/buildDockerSetupPayload';
import { buildDockerRegistryPayload } from '../components/docker/registry/buildDockerRegistryPayload';
import { KubernetesRbacFormValues } from '../components/kubernetes/rbac/types';
import { KubernetesSecurityTypeSpecificData } from '../components/kubernetes/security/types';
import { KubernetesSetupFormData } from '../components/kubernetes/setup/types';
import { KubernetesRegistryFormValues } from '../components/kubernetes/registry/types';
import { DockerRBACFormData } from '../components/docker/rbac/types';
import { DockerSecurityFormData } from '../components/docker/security/types';
import { DockerSetupFormData } from '../components/docker/setup/types';
import { DockerRegistryFormData } from '../components/docker/registry/types';
import { CreatePolicyData } from '../queries/types';
import { PolicyFormByType } from '../components/PolicyFormByType';
import { hasSelectedGroupsWithConflicts } from '../components/PolicyConfigurationFormSection';

import { PolicyTypeSelectionStep } from './PolicySelectionStep';

const WIZARD_STEPS = [{ label: 'Select policy' }, { label: 'Review and edit' }];

type Step = 'select-type' | 'configure';

export function PolicyCreateView() {
  const router = useRouter();
  const urlPolicyType = router.globals.params?.type as PolicyType | undefined;

  // Wizard state
  const [step, setStep] = useState<Step>(
    urlPolicyType && isValidPolicyType(urlPolicyType)
      ? 'configure'
      : 'select-type'
  );
  const [selectedType, setSelectedType] = useState<PolicyType | null>(
    urlPolicyType && isValidPolicyType(urlPolicyType) ? urlPolicyType : null
  );

  // Filter states hoisted from PolicyTypeSelector
  const [environmentTypeFilter, setEnvironmentTypeFilter] =
    useState<EnvironmentTypeFilter>('compatible');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Track selected template (if any)
  const [selectedTemplate, setSelectedTemplate] =
    useState<PolicyTemplate | null>(null);

  // Data for type selector
  const { environments } = useEnvironmentList();
  const groupsQuery = useGroups();
  const groups = useMemo(() => groupsQuery.data || [], [groupsQuery.data]);

  const createMutation = useCreatePolicyMutation();

  // Derive the displayed policy type from filters
  const derivedPolicyType = useDerivedPolicyType(
    environments,
    groups,
    environmentTypeFilter,
    categoryFilter,
    searchQuery,
    selectedType
  );

  // Wizard navigation
  const currentStepIndex = step === 'select-type' ? 0 : 1;
  const canGoBack = step === 'configure';

  function goToNextStep() {
    if (step === 'select-type' && derivedPolicyType) {
      setSelectedType(derivedPolicyType);
      setStep('configure');
    }
  }

  function goToPreviousStep() {
    if (step === 'configure') {
      setStep('select-type');
    }
  }

  const title = getTitle();

  return (
    <div className="form-horizontal pb-20">
      <PageHeader
        title={title}
        breadcrumbs={[{ label: 'Policies', link: 'portainer.policies' }, title]}
        reload
      />

      {/* Stepper - always visible */}
      <div className="row">
        <div className="col-sm-12">
          <Stepper
            steps={WIZARD_STEPS}
            currentStepIndex={currentStepIndex}
            onStepClick={(index) => {
              if (index === 0) setStep('select-type');
              if (index === 1 && derivedPolicyType) {
                setSelectedType(derivedPolicyType);
                setStep('configure');
              }
            }}
          />
        </div>
      </div>

      {/* Step 1: Type Selection */}
      {step === 'select-type' && (
        <PolicyTypeSelectionStep
          value={derivedPolicyType}
          onChange={(type) => {
            setSelectedType(type);
            // Clear template when manually selecting a policy type
            setSelectedTemplate(null);
          }}
          onTemplateSelect={setSelectedTemplate}
          selectedTemplateId={selectedTemplate?.id}
          environments={environments}
          groups={groups}
          environmentFilter={environmentTypeFilter}
          onEnvironmentFilterChange={setEnvironmentTypeFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onNext={goToNextStep}
        />
      )}

      {/* Step 2: Configure */}
      {step === 'configure' && derivedPolicyType && (
        <div className="row">
          <div className="col-sm-12">
            <PolicyFormByType
              type={derivedPolicyType}
              mode="create"
              onSubmit={handleSubmit}
              templateData={selectedTemplate?.data}
              onBack={goToPreviousStep}
              canGoBack={canGoBack}
            />
          </div>
        </div>
      )}
    </div>
  );

  function getTitle() {
    if (step === 'configure' && derivedPolicyType) {
      return `Create ${getEnvironmentTypeDisplayName(
        getEnvironmentTypeFromPolicyType(derivedPolicyType)
      )} ${getPolicyTypeDisplayName(derivedPolicyType)} policy`;
    }
    return 'Create policy';
  }

  async function handleSubmit(values: unknown) {
    if (!derivedPolicyType) return;

    const formValues = values as {
      name: string;
      environmentGroupIds: Array<number>;
    };

    let payloadData: CreatePolicyData;
    try {
      payloadData = buildPolicyPayload(derivedPolicyType, values);
    } catch (error) {
      notifyError('Error', error as Error);
      return;
    }

    // Check if any selected groups have conflicts that need to be overridden
    const needsOverride = hasSelectedGroupsWithConflicts(
      formValues.environmentGroupIds,
      groups,
      derivedPolicyType
    );

    await createMutation.mutateAsync(
      {
        Name: formValues.name,
        Type: derivedPolicyType,
        EnvironmentType: getEnvironmentTypeFromPolicyType(derivedPolicyType),
        EnvironmentGroups: formValues.environmentGroupIds,
        Data: payloadData,
        AllowOverride: needsOverride,
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Policy created successfully');
          router.stateService.go('portainer.policies');
        },
      }
    );
  }
}

// Type guards for each form data type
function isKubernetesRbacFormData(
  value: unknown
): value is KubernetesRbacFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessList' in value &&
    Array.isArray((value as KubernetesRbacFormValues).accessList)
  );
}

function isKubernetesSecurityFormData(
  value: unknown
): value is KubernetesSecurityTypeSpecificData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'restrictDefaultNamespace' in value &&
    'enablePodSecurityConstraints' in value
  );
}

function isKubernetesSetupFormData(
  value: unknown
): value is KubernetesSetupFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'useLoadBalancer' in value &&
    'ingressControllers' in value &&
    'storageClasses' in value
  );
}

function isKubernetesRegistryFormData(
  value: unknown
): value is KubernetesRegistryFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessRows' in value &&
    Array.isArray((value as KubernetesRegistryFormValues).accessRows) &&
    ((value as KubernetesRegistryFormValues).accessRows.length === 0 ||
      'namespaces' in (value as KubernetesRegistryFormValues).accessRows[0])
  );
}

function isDockerRBACFormData(value: unknown): value is DockerRBACFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessList' in value &&
    Array.isArray((value as DockerRBACFormData).accessList)
  );
}

function isDockerSecurityFormData(
  value: unknown
): value is DockerSecurityFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'allowBindMountsForRegularUsers' in value &&
    'allowPrivilegedModeForRegularUsers' in value
  );
}

function isDockerSetupFormData(value: unknown): value is DockerSetupFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'enableHostManagementFeatures' in value &&
    'gpus' in value
  );
}

function isDockerRegistryFormData(
  value: unknown
): value is DockerRegistryFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessRows' in value &&
    Array.isArray((value as DockerRegistryFormData).accessRows) &&
    ((value as DockerRegistryFormData).accessRows.length === 0 ||
      ('userIds' in (value as DockerRegistryFormData).accessRows[0] &&
        'teamIds' in (value as DockerRegistryFormData).accessRows[0]))
  );
}

/**
 * Build the policy payload based on policy type.
 * Uses type guards to validate form values at runtime.
 */
function buildPolicyPayload(
  type: PolicyType,
  formValues: unknown
): CreatePolicyData {
  switch (type) {
    case 'rbac-k8s':
      if (!isKubernetesRbacFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes RBAC policy');
      }
      return buildKubernetesRBACPayload(formValues);
    case 'security-k8s':
      if (!isKubernetesSecurityFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Security policy');
      }
      return buildKubernetesSecurityPayload(formValues);
    case 'setup-k8s':
      if (!isKubernetesSetupFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Setup policy');
      }
      return buildKubernetesSetupPayload(formValues);
    case 'registry-k8s':
      if (!isKubernetesRegistryFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Registry policy');
      }
      return buildKubernetesRegistryPayload(formValues);
    case 'rbac-docker':
      if (!isDockerRBACFormData(formValues)) {
        throw new Error('Invalid form data for Docker RBAC policy');
      }
      return buildDockerRBACPayload(formValues);
    case 'security-docker':
      if (!isDockerSecurityFormData(formValues)) {
        throw new Error('Invalid form data for Docker Security policy');
      }
      return buildDockerSecurityPayload(formValues);
    case 'setup-docker':
      if (!isDockerSetupFormData(formValues)) {
        throw new Error('Invalid form data for Docker Setup policy');
      }
      return buildDockerSetupPayload(formValues);
    case 'registry-docker':
      if (!isDockerRegistryFormData(formValues)) {
        throw new Error('Invalid form data for Docker Registry policy');
      }
      return buildDockerRegistryPayload(formValues);
    default:
      throw new Error(`Unknown policy type: ${type}`);
  }
}

/**
 * Get selectable policy types (those with compatible groups)
 */
function getSelectablePolicyTypes(
  filteredPolicyTypes: Array<{ id: PolicyType }>,
  environments: Environment[],
  groups: EnvironmentGroup[]
): PolicyType[] {
  return filteredPolicyTypes
    .filter(
      (metadata) =>
        getCompatibleGroupsCount(metadata.id, environments, groups) > 0
    )
    .map((metadata) => metadata.id);
}

/**
 * Select the default policy type based on available options
 * Priority: 'rbac-k8s' → first selectable option → null
 */
function selectDefaultPolicyType(
  selectableOptions: PolicyType[]
): PolicyType | null {
  // Prefer rbac-k8s as the default
  if (selectableOptions.includes('rbac-k8s')) {
    return 'rbac-k8s';
  }

  // Fall back to first selectable option
  if (selectableOptions.length > 0) {
    return selectableOptions[0];
  }

  return null;
}

/**
 * Derives the appropriate policy type from server state and filters.
 *
 * Instead of syncing state with useEffect, we derive the valid selection from available options.
 * This avoids race conditions, extra renders, and preserves the original selection for restoration.
 *
 * Priority: current selection (if valid) → 'rbac-k8s' → first available option
 */
function useDerivedPolicyType(
  environments: Environment[],
  groups: EnvironmentGroup[],
  environmentFilter: EnvironmentTypeFilter,
  categoryFilter: CategoryFilter,
  searchQuery: string,
  currentPolicyType: PolicyType | null
): PolicyType | null {
  return useMemo(() => {
    // Get base policy types based on the environment filter
    const basePolicyTypes =
      environmentFilter === 'all'
        ? getAllPolicyTypesMetadata()
        : getVisiblePolicyTypes(environments, groups);

    // Apply filters using shared utility
    const filteredPolicyTypes = filterPolicyTypes(
      basePolicyTypes,
      environmentFilter,
      categoryFilter,
      searchQuery
    );

    // Get selectable options (those with compatible groups)
    const selectableOptions = getSelectablePolicyTypes(
      filteredPolicyTypes,
      environments,
      groups
    );

    const availableOptions = filteredPolicyTypes.map((metadata) => metadata.id);

    // Preserve the user's current selection if it is still visible, even if unsupported
    if (
      currentPolicyType &&
      availableOptions.includes(currentPolicyType as PolicyType)
    ) {
      return currentPolicyType;
    }

    // Select default from selectable options
    const defaultSelection = selectDefaultPolicyType(selectableOptions);
    if (defaultSelection) {
      return defaultSelection;
    }

    // As a last resort, return the first visible option (may be unsupported)
    return availableOptions[0] || null;
  }, [
    environments,
    groups,
    environmentFilter,
    categoryFilter,
    searchQuery,
    currentPolicyType,
  ]);
}

function isValidPolicyType(value: unknown): value is PolicyType {
  const validTypes: PolicyType[] = [
    'rbac-k8s',
    'security-k8s',
    'setup-k8s',
    'registry-k8s',
    'rbac-docker',
    'security-docker',
    'setup-docker',
    'registry-docker',
  ];
  return validTypes.includes(value as PolicyType);
}
